import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Truck, ShieldCheck, RotateCcw } from "lucide-react";
import api from "@/lib/api";
import Marquee from "@/components/Marquee";
import Manifesto from "@/components/Manifesto";
import BrandHouses from "@/components/BrandHouses";
import ProductCard from "@/components/ProductCard";
import { PageHeader } from "@/components/ui-bits";
import { useLang } from "@/lib/i18n";

const HERO_IMAGE =
  "https://static.prod-images.emergentagent.com/jobs/c861e2b1-8fd2-48dc-8916-86c886f62f77/images/36d43846431b87ff0ec6b0c8e5a2405b2930b71dbd544d8e7a3d4a3a151fd56e.jpeg";
const BOTTLE_IMAGE =
  "https://static.prod-images.emergentagent.com/jobs/c861e2b1-8fd2-48dc-8916-86c886f62f77/images/3d23fe005dd4cd4cfefd8ac7eeb7db1936e590a0eadd5917fc9dc88db572dbbf.jpeg";

const EASE = [0.16, 1, 0.3, 1];

const MaskedLine = ({ children, delay, entered }) => (
  <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={entered ? { y: 0 } : { y: "112%" }}
      transition={{ duration: 1.1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);



export const Home = ({ entered }) => {
  const navigate = useNavigate();
  const { t } = useLang();
  const SERVICE = [
    [<Truck size={16} key="t" />, t("home.s1t"), t("home.s1s")],
    [<ShieldCheck size={16} key="s" />, t("home.s2t"), t("home.s2s")],
    [<RotateCcw size={16} key="r" />, t("home.s3t"), t("home.s3s")],
  ];
  const [featured, setFeatured] = useState([]);
  const [brands, setBrands] = useState([]);
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 900], [0, 140]);
  const bgScale = useTransform(scrollY, [0, 900], [1, 1.08]);
  const bottleY = useTransform(scrollY, [0, 900], [0, -90]);
  const fade = useTransform(scrollY, [0, 500], [1, 0]);

  useEffect(() => {
    api.get("/home").then((d) => setFeatured(d.data.filter((p) => p.stock > 0).slice(0, 4))).catch(() => {});
    api.get("/brands").then((d) => setBrands(d.data)).catch(() => {});
  }, []);

  return (
    <>
      <section className="relative min-h-screen flex flex-col justify-end overflow-hidden">
        <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
          <img src={HERO_IMAGE} alt="DEO ATELIER atelier" className="w-full h-full object-cover opacity-50" />
          <div className="absolute inset-0 bg-gradient-to-b from-obsidian/70 via-obsidian/40 to-obsidian" />
          <div className="absolute inset-0 bg-gradient-to-r from-obsidian/80 via-transparent to-obsidian/40" />
        </motion.div>

        <motion.div style={{ y: bottleY }} className="hidden lg:block absolute right-[6%] top-[16%] w-[300px] xl:w-[340px] z-[5]">
          <motion.div
            initial={{ opacity: 0, y: 60, rotate: 4 }}
            animate={entered ? { opacity: 1, y: 0, rotate: 0 } : {}}
            transition={{ duration: 1.4, delay: 0.9, ease: EASE }}
            className="relative"
          >
            <motion.img
              src={BOTTLE_IMAGE}
              alt="AXE Dark Temptation"
              className="w-full rounded-sm shadow-[0_60px_120px_-30px_rgba(0,0,0,0.9)] border border-gold/20"
              animate={{ y: [0, -14, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            />
            <div className="absolute -inset-4 -z-10 bg-gold/10 blur-3xl rounded-full" />
          </motion.div>
        </motion.div>

        <motion.div style={{ opacity: fade }} className="relative z-10 px-6 sm:px-12 lg:px-20 pb-24 sm:pb-28 pt-40">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={entered ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
            className="font-mono text-[10px] sm:text-xs tracking-[0.35em] uppercase text-gold/85 mb-8"
          >
            {t("hero.eyebrow")}
          </motion.p>

          <h1 className="font-serif font-light uppercase text-cream text-5xl sm:text-7xl lg:text-8xl leading-[1.02] max-w-4xl">
            <MaskedLine entered={entered} delay={0.25}>{t("hero.line1")}</MaskedLine>
            <MaskedLine entered={entered} delay={0.4}>
              <span className="italic normal-case text-gold">{t("hero.line2")}</span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={entered ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 1, delay: 0.7, ease: EASE }}
            className="mt-8 max-w-xl text-sm sm:text-base leading-relaxed text-mist"
          >
            {t("hero.sub")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={entered ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 1, delay: 0.85, ease: EASE }}
            className="mt-12 flex flex-wrap items-center gap-5"
          >
            <button
              data-testid="hero-cta-collection"
              onClick={() => navigate("/winkel")}
              className="relative overflow-hidden bg-gold text-obsidian px-10 py-4 font-mono text-xs tracking-[0.3em] uppercase transition-transform duration-300 hover:-translate-y-0.5"
            >
              {t("hero.cta1")}
            </button>
            <button
              data-testid="hero-cta-house"
              onClick={() => document.querySelector("#huis")?.scrollIntoView({ behavior: "smooth" })}
              className="border border-cream/25 text-cream px-10 py-4 font-mono text-xs tracking-[0.3em] uppercase hover:border-gold hover:text-gold transition-colors duration-300"
            >
              {t("hero.cta2")}
            </button>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={entered ? { opacity: 1 } : {}}
            transition={{ duration: 1.2, delay: 1.1 }}
            className="mt-14 font-mono text-[10px] tracking-[0.3em] uppercase text-smoke"
          >
            {t("hero.stats")}
          </motion.p>
        </motion.div>
      </section>

      <Marquee />

      <section className="px-6 sm:px-12 lg:px-20 py-24 lg:py-32">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <PageHeader eyebrow={t("home.eyebrow")} title={<>{t("home.title1")} <span className="italic normal-case text-gold">{t("home.title2")}</span></>} />
          <button
            data-testid="home-view-all"
            onClick={() => navigate("/winkel")}
            className="border border-cream/25 text-cream px-8 py-3.5 font-mono text-[11px] tracking-[0.3em] uppercase hover:border-gold hover:text-gold transition-colors duration-300"
          >
            {t("home.viewAll")}
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {featured.map((p, i) => (
            <ProductCard key={p.slug} product={p} index={i} />
          ))}
        </div>
      </section>

      <section className="border-y border-gold/10 bg-ink/40 px-6 sm:px-12 lg:px-20 py-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {SERVICE.map(([icon, title, sub]) => (
            <div key={title} className="flex items-center gap-4">
              <span className="flex h-11 w-11 items-center justify-center rounded-full border border-gold/30 text-gold shrink-0">{icon}</span>
              <div>
                <p className="font-serif text-lg text-cream">{title}</p>
                <p className="text-xs text-mist mt-0.5">{sub}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <Manifesto />
      <BrandHouses brands={brands} onSelect={(slug) => navigate(`/winkel?brand=${slug}`)} />
    </>
  );
};

export default Home;
